/**
 * API Configuration
 *
 * Automatically detects the correct API URL based on environment:
 * - Local development: http://localhost:3000/api (via Vite proxy)
 * - Firebase Hosting (production/staging/custom domains): Same-origin via rewrites
 */

/**
 * Get the API base URL based on current environment
 */
export function getApiUrl(): string {
  // Check for explicit override in environment variables
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // If hosted on GCS or a custom domain that might not be same-origin,
  // we need to use the absolute backend URL.
  // NOTE: c.swisstax.work is now proxied by the Cloudflare Worker, so it can use same-origin.
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (
      hostname === "storage.googleapis.com" ||
      hostname === "corp.swisstax.work"
    ) {
      return "https://c-swisstax-work.web.app/api";
    }
  }

  // All other environments use same-origin API via Firebase Hosting rewrites or Vite proxy
  return "/api";
}

/**
 * Get the tRPC endpoint URL
 */
export function getTrpcUrl(): string {
  return getApiUrl();
}

/**
 * Check if running in production environment
 */
export function isProduction(): boolean {
  if (typeof window === "undefined") return false;

  const hostname = window.location.hostname;
  return (
    import.meta.env.MODE === "production" &&
    (hostname === "c.swisstax.work" ||
      hostname === "corp.swisstax.work" ||
      hostname.endsWith(".c.swisstax.work") ||
      hostname === "c-swisstax-work.web.app" ||
      hostname === "storage.googleapis.com")
  );
}

/**
 * Check if running in staging environment
 */
export function isStaging(): boolean {
  const hostname = window.location.hostname;
  return (
    hostname.includes("c-swisstax-work-staging") ||
    hostname === "c-swisstax-work.firebaseapp.com"
  );
}

/**
 * Check if running in local development
 */
export function isDevelopment(): boolean {
  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1";
}

/**
 * Get environment name
 */
export function getEnvironment(): "production" | "staging" | "development" {
  if (isProduction()) return "production";
  if (isStaging()) return "staging";
  return "development";
}

/**
 * API configuration object
 */
export const apiConfig = {
  baseUrl: getApiUrl(),
  trpcUrl: getTrpcUrl(),
  environment: getEnvironment(),
  isProduction: isProduction(),
  isStaging: isStaging(),
  isDevelopment: isDevelopment(),
} as const;

// Log API configuration in development
if (typeof window !== "undefined" && isDevelopment()) {
  console.log("[API Config]", apiConfig);
}
